import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useId, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useUserQuery } from '../../../../queries';
import { Button } from '../../../components-v2/Button';
import { DateRangePicker } from '../../../components-v2/DateRangePicker';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { Select, SelectCheckboxItem, SelectContent, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { UserSelectModal } from '../../UserSelectModal';
import { allBlogStatusFilterValues, blogStatusLabel } from './blogStatusFilters';
import { styleSheet } from './styles';

interface IProps {
	from: string;
	impersonationContext?: Api.IImpersonationContext;
	onClearFilters: () => void;
	onDateRangeChanged: (from: string, to: string) => void;
	onSentByChanged: (userId: string | null) => void;
	onStatusChanged: (status: Api.BlogStatus[]) => void;
	sentBy: string | null;
	status: Api.BlogStatus[];
	to: string;
}

export const BlogReportingToolbar = ({
	from,
	impersonationContext,
	onClearFilters,
	onDateRangeChanged,
	onSentByChanged,
	onStatusChanged,
	sentBy,
	status,
	to,
}: IProps) => {
	const id = useId();
	const userSession = useUserSession();
	const [showingSentByModal, setShowingSentByModal] = useState(false);
	// Reading someone else's account, the signed-in user is nobody on it, so "me" is not a sender it can have.
	const isReadingOwnAccount = !impersonationContext?.account?.id;
	const isSelf = isReadingOwnAccount && sentBy === userSession.user.id;
	const canPickEmployee = userSession.isAdmin || !isReadingOwnAccount;
	const userQuery = useUserQuery({
		// `useUserQuery` declares `id: string`; `sentBy` is nullable. `strictNullChecks` is off in this package,
		// so the mismatch type-checks silently — coerce rather than rely on `enabled` alone to keep it honest.
		enabled: Boolean(sentBy) && !isSelf,
		id: sentBy ?? '',
		impersonationContext,
	});

	let senderLabel = '...';
	if (!sentBy) {
		senderLabel = 'All Employees';
	} else if (isSelf) {
		senderLabel = 'Me';
	} else if (userQuery.data) {
		senderLabel = getDisplayName(userQuery.data);
	}

	const isAllStatuses = status.length === allBlogStatusFilterValues.length;
	const isCustomFilter = !isAllStatuses || Boolean(sentBy) || Boolean(from) || Boolean(to);

	return (
		<div className={css(styleSheet.filter)}>
			<span className={css(styleSheet.filterLabel)}>Filter By:</span>
			<Select multiple value={status} onValueChange={onStatusChanged}>
				<SelectTrigger styles={[styleSheet.filterTrigger]}>
					<SelectValue>{isAllStatuses ? 'All' : status.map(blogStatusLabel).join(', ')}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					{allBlogStatusFilterValues.map(value => (
						<SelectCheckboxItem key={`${id}-status-${value}`} value={value}>
							{blogStatusLabel(value)}
						</SelectCheckboxItem>
					))}
				</SelectContent>
			</Select>
			<DateRangePicker
				ariaLabel='Filter by scheduled date'
				from={from}
				numberOfMonths={1}
				onChange={onDateRangeChanged}
				size='sm'
				to={to}
			/>
			<Dropdown>
				<DropdownSelectTrigger styles={[styleSheet.filterTrigger]}>Sent by {senderLabel}</DropdownSelectTrigger>
				<DropdownContent>
					{canPickEmployee ? (
						<DropdownItem
							onClick={() => {
								if (sentBy != null) {
									onSentByChanged(null);
								}
							}}
						>
							All Employees
						</DropdownItem>
					) : null}
					{isReadingOwnAccount ? (
						<DropdownItem
							onClick={() => {
								if (sentBy !== userSession.user.id) {
									onSentByChanged(userSession.user.id);
								}
							}}
						>
							Sent by Me
						</DropdownItem>
					) : null}
					{canPickEmployee ? (
						<DropdownItem onClick={() => setShowingSentByModal(true)}>Select an Employee</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
			{isCustomFilter ? (
				<Button onClick={onClearFilters} variant='secondary'>
					Clear filters
				</Button>
			) : null}
			<UserSelectModal
				impersonationContext={impersonationContext}
				modalProps={{
					isOpen: showingSentByModal,
					onRequestClose: (user?: Api.UserViewModel, cancel?: boolean) => {
						if (user && !cancel) {
							onSentByChanged(user.id);
						}
						setShowingSentByModal(false);
					},
				}}
				title='Select an Employee'
			/>
		</div>
	);
};
